import { useEffect, useId, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FolderPlus, Pencil, Users, UserMinus, Tag, Download } from 'lucide-react';
import { exportsApi, saveBlob, ExportError } from '../../../../api/exports';
import { projectsApi, projectTypesApi, ProjectDetail } from '../../../../api/projects';
import { Project, ProjectType, PermissionEnum, NonBillableClassification } from '../../../../types';
import { useAuth } from '../../../../contexts/AuthContext';
import { formatDate } from '../../../../lib/utils';
import { useDebouncedValue } from '../../../../lib/useDebouncedValue';
import { routesToDirectManager } from '../../../../lib/timeEntryHelpers';
import { Button } from '../../../../components/ui/atoms/button';
import { Input } from '../../../../components/ui/atoms/input';
import { Badge } from '../../../../components/ui/atoms/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, SortableTableHead } from '../../../../components/ui/molecules/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../../components/ui/organisms/dialog';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '../../../../components/ui/organisms/form';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../../components/ui/molecules/select';
import { SearchableSelect } from '../../../../components/ui/molecules/searchable-select';
import { UserCombobox } from '../../../../components/ui/molecules/user-combobox';
import { Loading } from '../components';
import { getErrorMessage, refreshProjectRelatedQueries } from '../utils';

// ─── schemas ──────────────────────────────────────────────────────────────────

const createSchema = z.object({
  name: z.string().min(6, 'Name must be at least 6 characters'),
  client: z.string().optional(),
  typeId: z.string().min(1, 'Project type is required'),
  billable: z.boolean(),
  nonBillableClassification: z.enum(['POC', 'INTERNAL', 'OTHER']),
  primaryScrumId: z.string().optional(),
  secondaryScrumId: z.string().optional(),
  projectLeadId: z.string().optional(),
}).superRefine((data, ctx) => {
  if (!data.projectLeadId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Project lead is required', path: ['projectLeadId'] });
  }
  if (data.billable && !data.primaryScrumId) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Primary scrum master is required', path: ['primaryScrumId'] });
  }
});

/**
 * Built per project, because the approver rules don't apply to all of them.
 * Non-Project-Related routes to each employee's own direct manager and never
 * enters the scrum → lead chain, so it has no scrum or lead to require —
 * demanding one there would mean assigning an approver who is never consulted.
 */
const editSchemaFor = (projectId: string) =>
  z.object({
    name: z.string().min(6, 'Name must be at least 6 characters'),
    client: z.string().optional(),
    typeId: z.string().min(1, 'Project type is required'),
    billable: z.boolean(),
    nonBillableClassification: z.enum(['POC', 'INTERNAL', 'OTHER']),
    primaryScrumId: z.string().optional(),
    secondaryScrumId: z.string().optional(),
    projectLeadId: z.string().optional(),
    status: z.enum(['ACTIVE', 'CLOSED', 'FROZEN']),
  }).superRefine((data, ctx) => {
    if (routesToDirectManager(projectId)) return;
    // The lead is the terminal approver, and on a non-billable project with no
    // scrum they're the only one — entries there start at PENDING_LEAD. Without
    // a lead a project collects entries nobody can see or approve.
    if (!data.projectLeadId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Project lead is required', path: ['projectLeadId'] });
    }
    // Scrum stays mandatory on billable work; non-billable projects may skip it
    // and route straight to the lead.
    if (data.billable && !data.primaryScrumId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Primary scrum master is required', path: ['primaryScrumId'] });
    }
  });

type CreateValues = z.infer<typeof createSchema>;
type EditValues = z.infer<ReturnType<typeof editSchemaFor>>;
/**
 * POC / Internal are optional, mutually exclusive labels and only apply to
 * non-billable projects, so nothing renders while Billable is ticked.
 */
function ClassificationFields({ billable, value, onChange }: {
  billable: boolean;
  value: NonBillableClassification;
  onChange: (next: NonBillableClassification) => void;
}) {
  const groupName = useId();
  if (billable) return null;
  const options: { label: string; value: NonBillableClassification }[] = [
    { label: 'POC', value: 'POC' },
    { label: 'Internal Project', value: 'INTERNAL' },
    { label: 'Other', value: 'OTHER' },
  ];
  return (
    <div className="space-y-2">
      <span id={`${groupName}-label`} className="text-sm font-medium leading-none">Non Billable Classification</span>
      <div role="radiogroup" aria-labelledby={`${groupName}-label`} className="flex items-center gap-6">
        {options.map((o) => (
          <label key={o.value} className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="radio"
              name={groupName}
              className="h-4 w-4 accent-primary"
              checked={value === o.value}
              onChange={() => onChange(o.value)}
            />
            {o.label}
          </label>
        ))}
      </div>
    </div>
  );
}

// ─── Create project dialog ────────────────────────────────────────────────────

export function CreateProjectDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  
  const { data: typesData } = useQuery({
    queryKey: ['project-types'],
    queryFn: () => projectTypesApi.list().then((r) => r.data),
  });
  const types: ProjectType[] = typesData ?? [];
  
  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: '', client: '', typeId: '', billable: false, nonBillableClassification: 'OTHER', primaryScrumId: '', secondaryScrumId: '', projectLeadId: '' },
  });
  
  const [billable, nonBillableClassification] = form.watch(['billable', 'nonBillableClassification']);
  
  const mutation = useMutation({
    mutationFn: (v: CreateValues) =>
      projectsApi.create({
        name: v.name,
        client: v.client || undefined,
        typeId: v.typeId,
        billable: v.billable,
        nonBillableClassification: v.nonBillableClassification,
        primaryScrumId: v.primaryScrumId || undefined,
        secondaryScrumId: v.secondaryScrumId || undefined,
        projectLeadId: v.projectLeadId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      form.reset();
      setOpen(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <FolderPlus className="mr-2 h-4 w-4" />
          New Project
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Project</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project Name</FormLabel>
                  <FormControl><Input placeholder="e.g. Website Redesign" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="typeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project Type</FormLabel>
                  {types.length > 0 ? (
                    <FormControl>
                      <SearchableSelect
                        value={field.value}
                        onValueChange={field.onChange}
                        options={types.map((t) => ({ value: t.id, label: t.name }))}
                        placeholder="Select a type…"
                        searchPlaceholder="Search project types..."
                      />
                    </FormControl>
                  ) : (
                    <p className="text-sm text-muted-foreground rounded-md border border-dashed px-3 py-2">
                      No project types exist yet. Create one in the Project Types tab first.
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="client"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client <span className="text-muted-foreground">(optional)</span></FormLabel>
                  <FormControl><Input placeholder="e.g. Acme Corp" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="billable"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl>
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        if (e.target.checked) form.setValue('nonBillableClassification', 'OTHER');
                      }}
                    />
                  </FormControl>
                  <FormLabel className="cursor-pointer font-normal">Billable project</FormLabel>
                </FormItem>
              )}
            />
            <ClassificationFields
              billable={billable}
              value={nonBillableClassification}
              onChange={(next) => form.setValue('nonBillableClassification', next)}
            />
            {(
              <>
                <FormField
              control={form.control}
              name="primaryScrumId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Primary Scrum Master{!billable && <span className="text-muted-foreground"> (optional)</span>}
                  </FormLabel>
                  <FormControl>
                    <UserCombobox value={field.value ?? ''} onValueChange={field.onChange} allowEmpty={!billable} />
                  </FormControl>
                  {!billable && (
                    <p className="text-xs text-muted-foreground">
                      Leave blank to send entries straight to the project lead.
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="secondaryScrumId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Secondary Scrum Master <span className="text-muted-foreground">(optional)</span></FormLabel>
                  <FormControl>
                    <UserCombobox value={field.value ?? ''} onValueChange={field.onChange} allowEmpty />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="projectLeadId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project Lead</FormLabel>
                  <FormControl>
                    <UserCombobox value={field.value ?? ''} onValueChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> 
              </>
            )}
            
            {mutation.error && (
              <p className="text-sm text-destructive">
                {getErrorMessage(mutation.error, 'Failed to create project')}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Creating…' : 'Create Project'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit project dialog ──────────────────────────────────────────────────────

function EditProjectDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const queryClient = useQueryClient();

  const { data: typesData } = useQuery({
    queryKey: ['project-types'],
    queryFn: () => projectTypesApi.list().then((r) => r.data),
  });
  const types: ProjectType[] = typesData ?? [];

  // Non-project time goes to each employee's own manager, so this project has
  // no scrum or lead to set — the approver fields don't apply to it.
  const managerRouted = routesToDirectManager(project.id);

  const form = useForm<EditValues>({
    resolver: zodResolver(editSchemaFor(project.id)),
    defaultValues: {
      name: project.name,
      client: project.client ?? '',
      typeId: project.typeId ?? '',
      billable: project.billable,
      nonBillableClassification: project.nonBillableClassification ?? 'OTHER',
      primaryScrumId: project.primaryScrumId ?? '',
      secondaryScrumId: project.secondaryScrumId ?? '',
      projectLeadId: project.projectLeadId ?? '',
      status: project.status,
    },
  });

  const [billable, nonBillableClassification] = form.watch(['billable', 'nonBillableClassification']);

  const mutation = useMutation({
    mutationFn: (v: EditValues) =>
      projectsApi.update(project.id, {
        name: v.name,
        client: v.client || undefined,
        typeId: v.typeId,
        billable: v.billable,
        nonBillableClassification: v.nonBillableClassification,
        primaryScrumId: v.primaryScrumId || null,
        secondaryScrumId: v.secondaryScrumId || null,
        // Omitted rather than nulled: the lead is the terminal approver and
        // can be swapped but not cleared. Absent on the manager-routed project,
        // which has none to send.
        projectLeadId: v.projectLeadId || undefined,
        status: v.status,
      }),
    onSuccess: async ({ data: updatedProject }) => {
      await refreshProjectRelatedQueries(queryClient, updatedProject);
      onClose();
    },
  });

  return (
    <DialogContent>
      <DialogHeader className="pr-8">
        <DialogTitle className="min-w-0 break-words">Edit Project</DialogTitle>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Project Name</FormLabel>
                <FormControl><Input {...field} className="min-w-0" /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="client"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Client <span className="text-muted-foreground">(optional)</span></FormLabel>
                <FormControl><Input {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="typeId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Project Type</FormLabel>
                {types.length > 0 ? (
                  <FormControl>
                    <SearchableSelect
                      value={field.value}
                      onValueChange={field.onChange}
                      options={types.map((t) => ({ value: t.id, label: t.name }))}
                      placeholder="Select a type…"
                      searchPlaceholder="Search project types..."
                    />
                  </FormControl>
                ) : (
                  <p className="text-sm text-muted-foreground rounded-md border border-dashed px-3 py-2">
                    No project types exist yet. Create one in the Project Types tab first.
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger><SelectValue placeholder="Select status…" /></SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="CLOSED">Closed</SelectItem>
                    <SelectItem value="FROZEN">Frozen</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="billable"
            render={({ field }) => (
              <FormItem className="flex items-center gap-2 space-y-0">
                <FormControl>
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-primary"
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      if (e.target.checked) form.setValue('nonBillableClassification', 'OTHER');
                    }}
                  />
                </FormControl>
                <FormLabel className="cursor-pointer font-normal">Billable project</FormLabel>
              </FormItem>
            )}
          />
          <ClassificationFields
            billable={billable}
            value={nonBillableClassification}
            onChange={(next) => form.setValue('nonBillableClassification', next)}
          />
          {/* Shown for every project that runs the scrum → lead chain — which
              is all of them except the manager-routed one. */}
          {managerRouted ? (
            <p className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
              Entries on this project go to each employee's own direct manager, so it has
              no scrum master or project lead.
            </p>
          ) : (
            <>
              <FormField
            control={form.control}
            name="primaryScrumId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Primary Scrum Master{!billable && <span className="text-muted-foreground"> (optional)</span>}
                </FormLabel>
                <FormControl>
                  <UserCombobox
                    value={field.value ?? ''}
                    onValueChange={field.onChange}
                    selectedUser={project.primaryScrum}
                    allowEmpty={!billable}
                  />
                </FormControl>
                {!billable && (
                  <p className="text-xs text-muted-foreground">
                    Leave blank to send entries straight to the project lead.
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="secondaryScrumId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Secondary Scrum Master <span className="text-muted-foreground">(optional)</span></FormLabel>
                <FormControl>
                  <UserCombobox
                    value={field.value ?? ''}
                    onValueChange={field.onChange}
                    selectedUser={project.secondaryScrum}
                    allowEmpty
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="projectLeadId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Project Lead</FormLabel>
                <FormControl>
                  <UserCombobox
                    value={field.value ?? ''}
                    onValueChange={field.onChange}
                    selectedUser={project.projectLead}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          /> 
            </>
          )}
          
          {mutation.error && (
            <p className="text-sm text-destructive">
              {getErrorMessage(mutation.error, 'Failed to update project')}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Form>
    </DialogContent>
  );
}

// ─── Assign users dialog ──────────────────────────────────────────────────────

function AssignUsersDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState('');
  // The picker keeps its own search text. Changing this key starts it over, so a user who was just
  // added does not leave their search behind to filter the next pick.
  const [pickerKey, setPickerKey] = useState(0);

  const { data: detail, isLoading } = useQuery({
    queryKey: ['project-detail', project.id],
    queryFn: () => projectsApi.get(project.id).then((r) => r.data),
  });

  const assignedIds = new Set((detail as ProjectDetail | undefined)?.userProjects.map((up) => up.user.id) ?? []);

  const assign = useMutation({
    mutationFn: (userId: string) => projectsApi.assignUser(project.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-detail', project.id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['user-options'] });
      setSelectedUserId('');
      setPickerKey((key) => key + 1);
    },
  });

  const unassign = useMutation({
    mutationFn: (userId: string) => projectsApi.unassignUser(project.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-detail', project.id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['user-options'] });
    },
  });

  return (
    <DialogContent className="max-w-md">
      <DialogHeader className="pr-8">
        <DialogTitle className="min-w-0 [overflow-wrap:anywhere]">Manage members in {project.name}</DialogTitle>
      </DialogHeader>

      {isLoading ? (
        <Loading size="sm" />
      ) : (
        <div className="space-y-4">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">Assigned members</p>
            {assignedIds.size === 0 && (
              <p className="text-sm text-muted-foreground italic">No members assigned yet.</p>
            )}
            <div className='max-h-64 space-y-2 overflow-y-auto pr-1'>
              {(detail as ProjectDetail | undefined)?.userProjects.map(({ user }) => (
                <div key={user.id} className="flex items-center justify-between gap-2 rounded-md border px-3 py-2">
                  <div className="flex-1 min-w-0 [overflow-wrap:anywhere]">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 text-destructive hover:text-destructive"
                    disabled={unassign.isPending}
                    onClick={() => unassign.mutate(user.id)}
                  >
                    <UserMinus className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Add member</p>
            <div className="flex gap-2">
              <div className="min-w-0 flex-1">
                <UserCombobox
                  key={pickerKey}
                  value={selectedUserId}
                  onValueChange={setSelectedUserId}
                  excludeProjectId={project.id}
                  emptyMessage="No unassigned users found."
                  disabled={assign.isPending}
                  // Long names wrap onto more lines instead of overflowing the dialog.
                  className="h-auto min-h-10 text-left [&>span]:min-w-0 [&>span]:whitespace-normal [&>span]:[overflow-wrap:anywhere]"
                />
              </div>
              <Button
                className="shrink-0"
                disabled={!selectedUserId || assign.isPending}
                onClick={() => assign.mutate(selectedUserId)}
              >
                Assign
              </Button>
            </div>
            {assign.error && (
              <p className="text-sm text-destructive">
                {getErrorMessage(assign.error, 'Failed to assign user')}
              </p>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        </div>
      )}
    </DialogContent>
  );
}

// ─── Projects tab ─────────────────────────────────────────────────────────────

export function ProjectsTab() {
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [assignProject, setAssignProject] = useState<Project | null>(null);
  const { hasPermission } = useAuth();
  const canExport = hasPermission(PermissionEnum.EXPORT_DATA);
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [sortBy, setSortBy] = useState<'name' | 'client' | 'createdAt'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  function toggleSort(column: 'name' | 'client' | 'createdAt') {
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
  }

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sortBy, sortDir]);

  const { data, isLoading } = useQuery({
    queryKey: ['projects', page, debouncedSearch, sortBy, sortDir],
    queryFn: () =>
      projectsApi
        .listPaginated({ page, limit: 20, name: debouncedSearch || undefined, sortBy, sortDir })
        .then((r) => r.data),
  });

  const projects: Project[] = data?.data ?? [];
  const meta = data?.meta;

  // The backend builds the file from the same filters, sort and search as this table.
  async function exportView() {
    setExportMessage('');
    setIsExporting(true);
    try {
      const file = await exportsApi.projects({ name: debouncedSearch, sortBy, sortDir });
      saveBlob(file.blob, file.filename);
    } catch (error) {
      // Includes the "no projects match" case: the backend's message explains it.
      setExportMessage(error instanceof ExportError ? error.message : 'Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-2">
        <Input
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-56"
        />
        <div className="flex items-center gap-2">
          {exportMessage && <span className="text-xs text-muted-foreground">{exportMessage}</span>}
          {canExport && (
            <Button variant="outline" onClick={exportView} disabled={isExporting || isLoading}>
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? 'Exporting…' : 'Export'}
            </Button>
          )}
          <CreateProjectDialog />
        </div>
      </div>
      {isLoading ? (
        <Loading />
      ) : (
        <>
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTableHead sorted={sortBy === 'name' && sortDir} onSort={() => toggleSort('name')}>Name</SortableTableHead>
              <SortableTableHead sorted={sortBy === 'client' && sortDir} onSort={() => toggleSort('client')}>Client</SortableTableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <SortableTableHead sorted={sortBy === 'createdAt' && sortDir} onSort={() => toggleSort('createdAt')}>Created</SortableTableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {projects.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  {search ? 'No projects match your search.' : 'No projects yet. Create your first one.'}
                </TableCell>
              </TableRow>
            )}
            {projects.map((project) => (
              <TableRow key={project.id} className={project.status !== 'ACTIVE' ? 'opacity-50' : ''}>
                <TableCell className="font-medium">
                  {project.name}
                  {project.nonBillableClassification === 'POC' && <Badge variant="secondary" className="ml-2">POC</Badge>}
                  {project.nonBillableClassification === 'INTERNAL' && <Badge variant="secondary" className="ml-2">Internal</Badge>}
                </TableCell>
                <TableCell className="text-muted-foreground">{project.client ?? '—'}</TableCell>
                <TableCell>
                  {project.type ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      <Tag className="h-3 w-3" />
                      {project.type.name}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={project.status === 'ACTIVE' ? 'default' : 'secondary'}>
                    {project.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">{formatDate(project.createdAt)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Manage members"
                      onClick={() => setAssignProject(project)}
                    >
                      <Users className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Edit project"
                      onClick={() => setEditProject(project)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {meta && meta.totalPages > 1 && (
          <div className="flex items-center justify-end gap-2 pt-4">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">Page {meta.page} of {meta.totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        )}
        </>
      )}

      <Dialog open={!!editProject} onOpenChange={(o) => { if (!o) setEditProject(null); }}>
        {editProject && (
          <EditProjectDialog project={editProject} onClose={() => setEditProject(null)} />
        )}
      </Dialog>

      <Dialog open={!!assignProject} onOpenChange={(o) => { if (!o) setAssignProject(null); }}>
        {assignProject && (
          <AssignUsersDialog project={assignProject} onClose={() => setAssignProject(null)} />
        )}
      </Dialog>
    </>
  );
}
